import * as React from 'react'
import { useContext } from 'react'
import { Dialog } from '@highlight/ui'
import { X } from 'lucide-react'
import { ModalBackdrop } from './modal-backdrop'
import { ModalPopup } from './modal-popup'
import { ModalContext } from './modal-context'

interface ModalContentProps {
  className?: string
  backdropClassName?: string
  children: React.ReactNode
  showClose?: boolean
  open?: boolean
  initialFocus?: React.ComponentProps<typeof ModalPopup>['initialFocus']
  /** Portal target; defaults to `document.body`. */
  container?: HTMLElement | null
}

export function ModalContent({
  className,
  backdropClassName,
  children,
  showClose,
  open,
  initialFocus,
  container,
}: ModalContentProps) {
  const context = useContext(ModalContext)

  // When used within ModalRenderer (context exists), always render
  // When used standalone, respect the open prop
  const shouldRender = context ? true : open !== false

  if (!shouldRender) return null

  const isConfirm = context?.confirm ?? false

  const shouldShowClose = showClose !== undefined ? showClose : !isConfirm

  return (
    <Dialog.Portal container={container}>
      <ModalBackdrop className={backdropClassName} />
      {/* `true` keeps Base UI's first-tabbable focus; the shared popup's default is to focus itself. */}
      <ModalPopup className={className} initialFocus={initialFocus ?? true}>
        {shouldShowClose && (
          <Dialog.Close
            aria-label="Close dialog"
            shape="circle"
            size="xs"
            variant="secondary"
            className="absolute right-4 top-4 z-20 flex"
          >
            <X className="size-3 stroke-[length:var(--icon-stroke-width-bold)]" />
          </Dialog.Close>
        )}
        {children}
      </ModalPopup>
    </Dialog.Portal>
  )
}
